(() => {
  const { EmblaCarousel, Autoplay, Fade, ClassNames } = window.EmblaBundle || {};
  const registry = new WeakMap();

  const getViewport = (root) => {
    if (!root) return null;
    return root.classList.contains('carousel__viewport')
      ? root
      : root.querySelector('.carousel__viewport');
  };

  const getSlides = (viewport) => {
    const container = viewport?.querySelector('.carousel__container');
    if (!container) return [];
    return Array.from(container.children).filter((node) =>
      node.classList.contains('carousel__slide'),
    );
  };

  const attachCompat = (viewport, emblaApi) => {
    const slides = getSlides(viewport);

    const shim = {
      _emblaApi: emblaApi,
      get activeIndex() {
        return emblaApi.selectedScrollSnap();
      },
      get slides() {
        return getSlides(viewport);
      },
      slideTo(index, speed) {
        emblaApi.scrollTo(index, speed !== undefined);
      },
      slideNext(speed) {
        emblaApi.scrollNext(speed !== undefined);
      },
      slidePrev(speed) {
        emblaApi.scrollPrev(speed !== undefined);
      },
      update() {
        emblaApi.reInit();
      },
      destroy() {
        ThemeCarousel.destroy(viewport);
      },
      params: {
        noSwiping: false,
        noSwipingClass: 'carousel__slide',
      },
    };

    viewport._emblaApi = emblaApi;
    viewport.carousel = shim;

    const carouselRoot = viewport.closest('.carousel') || viewport.parentElement;
    if (carouselRoot) {
      carouselRoot._emblaApi = emblaApi;
      carouselRoot.carousel = shim;
    }

    return shim;
  };

  const wireNavigation = (root, emblaApi, { prev, next } = {}) => {
    const prevBtn =
      typeof prev === 'string' ? root.querySelector(prev) : prev;
    const nextBtn =
      typeof next === 'string' ? root.querySelector(next) : next;

    const onPrev = () => emblaApi.scrollPrev();
    const onNext = () => emblaApi.scrollNext();

    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);

    const toggleDisabled = () => {
      if (!emblaApi.canScrollPrev()) {
        prevBtn?.setAttribute('disabled', 'disabled');
        prevBtn?.classList.add('is-disabled', 'carousel-btn--disabled');
      } else {
        prevBtn?.removeAttribute('disabled');
        prevBtn?.classList.remove('is-disabled', 'carousel-btn--disabled');
      }

      if (!emblaApi.canScrollNext()) {
        nextBtn?.setAttribute('disabled', 'disabled');
        nextBtn?.classList.add('is-disabled', 'carousel-btn--disabled');
      } else {
        nextBtn?.removeAttribute('disabled');
        nextBtn?.classList.remove('is-disabled', 'carousel-btn--disabled');
      }
    };

    emblaApi.on('select', toggleDisabled);
    emblaApi.on('reInit', toggleDisabled);
    toggleDisabled();

    return () => {
      prevBtn?.removeEventListener('click', onPrev);
      nextBtn?.removeEventListener('click', onNext);
    };
  };

  const wireFractionPagination = (root, emblaApi, selector) => {
    const el = root.querySelector(selector);
    if (!el) return () => {};

    const update = () => {
      const current = emblaApi.selectedScrollSnap() + 1;
      const total = emblaApi.scrollSnapList().length;
      el.textContent = `${current} / ${total}`;
    };

    emblaApi.on('select', update);
    emblaApi.on('reInit', update);
    update();

    return () => {};
  };

  const wireBulletPagination = (root, emblaApi, selector) => {
    const el = root.querySelector(selector);
    if (!el) return () => {};

    const buttons = [];

    const render = () => {
      el.innerHTML = '';
      buttons.length = 0;
      emblaApi.scrollSnapList().forEach((_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'carousel-pagination__bullet';
        button.setAttribute('aria-label', `Go to slide ${index + 1}`);
        button.addEventListener('click', () => emblaApi.scrollTo(index));
        el.append(button);
        buttons.push(button);
      });
      update();
    };

    const update = () => {
      const selected = emblaApi.selectedScrollSnap();
      buttons.forEach((button, index) => {
        const isSelected = index === selected;
        button.classList.toggle('is-selected', isSelected);
        button.toggleAttribute('aria-current', isSelected);
      });
    };

    emblaApi.on('select', update);
    emblaApi.on('reInit', render);
    render();

    return () => {};
  };

  const wireProgress = (root, emblaApi, selector) => {
    const el =
      typeof selector === 'string' ? root.querySelector(selector) : selector;
    if (!el) return () => {};

    const update = () => {
      const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
      el.style.setProperty('--slider-progress', String(progress));

      const viewportNode = emblaApi.rootNode();
      const containerNode = emblaApi.containerNode();
      if (viewportNode && containerNode) {
        const thumb = Math.min(
          1,
          viewportNode.getBoundingClientRect().width /
            Math.max(1, containerNode.getBoundingClientRect().width),
        );
        el.style.setProperty('--slider-thumb-size', String(Math.max(thumb, 0.08)));
      }
    };

    emblaApi.on('scroll', update);
    emblaApi.on('reInit', update);
    emblaApi.on('select', update);
    update();

    return () => {};
  };

  const wireAutoplayHover = (root, emblaApi) => {
    const autoplay = emblaApi.plugins()?.autoplay;
    if (!autoplay) return () => {};

    const onEnter = () => autoplay.stop();
    const onLeave = () => autoplay.play();

    root.addEventListener('mouseenter', onEnter);
    root.addEventListener('mouseleave', onLeave);

    return () => {
      root.removeEventListener('mouseenter', onEnter);
      root.removeEventListener('mouseleave', onLeave);
    };
  };

  const syncCarousels = (primary, secondary, { duration = false } = {}) => {
    let syncing = false;

    const syncTo = (source, target) => {
      if (syncing) return;
      syncing = true;
      target.scrollTo(source.selectedScrollSnap(), duration);
      syncing = false;
    };

    primary.on('select', () => syncTo(primary, secondary));
    secondary.on('select', () => syncTo(secondary, primary));
  };

  const ThemeCarousel = {
    create(root, options = {}, pluginFactories = [], hooks = {}) {
      if (!EmblaCarousel) {
        console.warn('Embla bundle not loaded');
        return null;
      }

      const viewport = getViewport(root) || root;
      if (!viewport) return null;

      ThemeCarousel.destroy(viewport);

      const plugins = pluginFactories.map((factory) =>
        typeof factory === 'function' ? factory() : factory,
      );

      const emblaApi = EmblaCarousel(viewport, options, plugins);
      const shim = attachCompat(viewport, emblaApi);
      const cleanups = [];

      if (hooks.prev || hooks.next) {
        cleanups.push(
          wireNavigation(viewport.closest('.carousel') || viewport, emblaApi, hooks),
        );
      }

      if (hooks.fraction) {
        cleanups.push(
          wireFractionPagination(
            viewport.closest('.carousel') || viewport,
            emblaApi,
            hooks.fraction,
          ),
        );
      }

      if (hooks.bullets) {
        cleanups.push(
          wireBulletPagination(
            viewport.closest('.carousel') || viewport,
            emblaApi,
            hooks.bullets,
          ),
        );
      }

      if (hooks.progress) {
        cleanups.push(
          wireProgress(
            viewport.closest('.carousel') || viewport,
            emblaApi,
            hooks.progress,
          ),
        );
      }

      if (hooks.autoplayHover) {
        cleanups.push(
          wireAutoplayHover(viewport.closest('.carousel') || viewport, emblaApi),
        );
      }

      if (typeof hooks.onInit === 'function') {
        hooks.onInit(emblaApi, shim, viewport);
      }

      registry.set(viewport, { emblaApi, cleanups, hooks });

      return emblaApi;
    },

    destroy(root) {
      const viewport = getViewport(root) || root;
      const entry = registry.get(viewport);
      if (!entry) return;

      entry.cleanups.forEach((cleanup) => cleanup());
      if (typeof entry.hooks?.onDestroy === 'function') {
        entry.hooks.onDestroy(entry.emblaApi, viewport);
      }

      entry.emblaApi.destroy();
      registry.delete(viewport);

      delete viewport._emblaApi;
      delete viewport.carousel;

      const carouselRoot = viewport.closest('.carousel');
      if (carouselRoot) {
        delete carouselRoot._emblaApi;
        delete carouselRoot.carousel;
      }
    },

    get(viewport) {
      return registry.get(getViewport(viewport) || viewport)?.emblaApi || null;
    },

    sync: syncCarousels,
    plugins: { Autoplay, Fade, ClassNames },
  };

  window.ThemeCarousel = ThemeCarousel;
})();
